import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, MenuItem, Paper, Stack, TextField, Typography,
} from '@mui/material';
import { ReceiptService } from '../../services/receipt.service';
import {
  Receipt, ReceiptEditDTO, ReceiptEditHistory, ReceiptMethod, ReceiptStatus,
} from '../../interfaces/sales/receipt.interface';
import { ReverseConfirmDialog } from '../procurement/ReverseConfirmDialog';
import { EditHistoryPanel, EditHistoryRow } from '../procurement/EditHistoryPanel';
import { AccountService } from '../../services/account.service';
import { Account } from '../../interfaces/accounting.interface';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { useToast } from '../../context/ToastContext';

const today = () => new Date().toISOString().slice(0, 10);

const RECEIPT_METHODS: { value: ReceiptMethod; label: string }[] = [
  { value: 'CASH', label: 'Cash' },
  { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'MOBILE_BANKING', label: 'Mobile Banking' },
  { value: 'OTHER', label: 'Other' },
];

const STATUS_COLORS: Record<ReceiptStatus, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  POSTED: 'success',
  REVERSED: 'error',
};

const mapToEditHistoryRow = (e: ReceiptEditHistory): EditHistoryRow => {
  const parts: string[] = [
    `amount was ${e.prevAmount}`,
    `date was ${e.prevReceiptDate}`,
    `method was ${e.prevMethod}`,
    `deposit account was ${e.prevDepositAccountId}`,
  ];
  if (e.prevReference) parts.push(`ref was ${e.prevReference}`);
  return {
    editSeq: e.editSeq,
    editedAt: e.editedAt,
    editReason: e.editReason,
    summary: parts.join('; ') || '—',
  };
};

const ReceiptDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const [loading, setLoading] = useState(true);
  const [editHistoryRows, setEditHistoryRows] = useState<EditHistoryRow[]>([]);

  // Reverse state
  const [reverseDialogOpen, setReverseDialogOpen] = useState(false);
  const [reversing, setReversing] = useState(false);

  // Edit modal state
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [assetAccounts, setAssetAccounts] = useState<Account[]>([]);

  // Edit form fields
  const [editAmount, setEditAmount] = useState<number>(NaN);
  const [editDate, setEditDate] = useState(today());
  const [editDepositAccountId, setEditDepositAccountId] = useState('');
  const [editMethod, setEditMethod] = useState<ReceiptMethod>('CASH');
  const [editReference, setEditReference] = useState('');
  const [editReason, setEditReason] = useState('');

  const loadData = useCallback(() => {
    if (!id) return;
    setLoading(true);
    ReceiptService.getById(id)
      .then(r => {
        setReceipt(r);
        return ReceiptService.editHistory(id)
          .then((rows: ReceiptEditHistory[]) => {
            setEditHistoryRows(rows.map(mapToEditHistoryRow));
          })
          .catch(() => setEditHistoryRows([]));
      })
      .catch(e => toast.showApiError(e, 'Failed to load receipt'))
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load ASSET accounts for the edit modal
  useEffect(() => {
    AccountService.getAccountsByType('ASSET')
      .then((accounts: Account[]) => setAssetAccounts(accounts))
      .catch((e: unknown) => toast.showApiError(e, 'Failed to load accounts'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openEditModal = () => {
    if (!receipt) return;
    setEditAmount(receipt.amount);
    setEditDate(receipt.receiptDate);
    setEditDepositAccountId(receipt.depositAccountId);
    setEditMethod(receipt.method);
    setEditReference(receipt.reference ?? '');
    setEditReason('');
    setEditOpen(true);
  };

  const handleReverse = async () => {
    if (!id) return;
    setReversing(true);
    try {
      const updated = await ReceiptService.reverse(id);
      setReceipt(updated);
      toast.showSuccess('Receipt reversed');
      setReverseDialogOpen(false);
    } catch (e) {
      toast.showApiError(e, 'Failed to reverse receipt');
    } finally {
      setReversing(false);
    }
  };

  const handleEditSubmit = async () => {
    if (!id || !editReason.trim()) return;
    setSaving(true);
    try {
      const dto: ReceiptEditDTO = {
        editReason: editReason.trim(),
        amount: editAmount,
        receiptDate: editDate,
        method: editMethod,
        reference: editReference.trim() || undefined,
        depositAccountId: editDepositAccountId,
      };
      await ReceiptService.edit(id, dto);
      toast.showSuccess('Receipt updated');
      setEditOpen(false);
      loadData();
    } catch (e) {
      toast.showApiError(e, 'Failed to update receipt');
      // Keep modal open so user can correct
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  if (!receipt) return <Box sx={{ p: 3 }}><Typography>Receipt not found.</Typography></Box>;

  const isReversed = receipt.status === 'REVERSED';
  const isEdited = editHistoryRows.length > 0;

  const editAmountValid = Number.isFinite(editAmount) && editAmount > 0;
  const editSubmitDisabled = saving || !editAmountValid || !editDepositAccountId || !editReason.trim();

  return (
    <Box sx={{ p: 3 }}>
      {/* Header row */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h5">{receipt.receiptNumber ?? '—'}</Typography>
          <Chip label={receipt.status} color={STATUS_COLORS[receipt.status]} size="small" />
          {isEdited && <Chip label="Edited" color="warning" size="small" />}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {!isReversed && (
            <Button variant="outlined" onClick={openEditModal}>
              Edit
            </Button>
          )}
          {!isReversed && (
            <Button variant="outlined" color="error" onClick={() => setReverseDialogOpen(true)}>
              Reverse
            </Button>
          )}
          <Button variant="outlined" onClick={() => navigate(-1)}>Back</Button>
        </Box>
      </Box>

      {/* Detail card */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">Receipt Number</Typography>
            <Typography>{receipt.receiptNumber ?? '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Status</Typography>
            <Chip label={receipt.status} color={STATUS_COLORS[receipt.status]} size="small" />
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Sales Invoice</Typography>
            <Typography
              component={RouterLink}
              to={`/sales/invoices/${receipt.salesInvoiceId}`}
              sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              {receipt.salesInvoiceId}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Amount</Typography>
            <Typography>{Number(receipt.amount).toFixed(2)}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Receipt Date</Typography>
            <Typography>{receipt.receiptDate}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Deposit Account</Typography>
            <Typography>{receipt.depositAccountId}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Method</Typography>
            <Typography>{receipt.method}</Typography>
          </Box>
          {receipt.reference && (
            <Box>
              <Typography variant="body2" color="text.secondary">Reference</Typography>
              <Typography>{receipt.reference}</Typography>
            </Box>
          )}
          {receipt.journalEntryId && (
            <Box>
              <Typography variant="body2" color="text.secondary">Journal Entry</Typography>
              <Typography
                component={RouterLink}
                to={`/accounting/journal-entries/${receipt.journalEntryId}`}
                sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                {receipt.journalEntryId}
              </Typography>
            </Box>
          )}
          {receipt.reversalJournalEntryId && (
            <Box>
              <Typography variant="body2" color="text.secondary">Reversal Journal Entry</Typography>
              <Typography
                component={RouterLink}
                to={`/accounting/journal-entries/${receipt.reversalJournalEntryId}`}
                sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                {receipt.reversalJournalEntryId}
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Edit history panel */}
      <EditHistoryPanel title="Receipt edit history" rows={editHistoryRows} />

      {/* Reverse confirm dialog */}
      <ReverseConfirmDialog
        open={reverseDialogOpen}
        entityLabel="Receipt"
        consequence="Posts a reversing journal entry dated today and re-opens the invoice balance."
        onCancel={() => setReverseDialogOpen(false)}
        onConfirm={handleReverse}
        loading={reversing}
      />

      {/* Edit modal */}
      <Dialog open={editOpen} onClose={() => !saving && setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Receipt</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {/* Amount */}
            <TextField
              label="Amount"
              type="number"
              value={Number.isFinite(editAmount) ? editAmount : ''}
              onChange={(e) => setEditAmount(e.target.value === '' ? NaN : Number(e.target.value))}
              inputProps={{ min: 0, step: 'any' }}
              fullWidth
              required
              error={Number.isFinite(editAmount) && !editAmountValid}
              helperText={Number.isFinite(editAmount) && !editAmountValid ? 'Amount must be greater than 0' : undefined}
            />

            {/* Receipt Date */}
            <TextField
              label="Receipt Date"
              type="date"
              value={editDate}
              onChange={(e) => setEditDate(e.target.value)}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
            />

            {/* Deposit Account (ASSET accounts) */}
            <AccountAutocomplete
              accounts={assetAccounts}
              value={editDepositAccountId}
              onChange={setEditDepositAccountId}
              label="Deposit Account"
              required
            />

            {/* Method */}
            <TextField
              select
              label="Method"
              value={editMethod}
              onChange={(e) => setEditMethod(e.target.value as ReceiptMethod)}
              fullWidth
              required
            >
              {RECEIPT_METHODS.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </TextField>

            {/* Reference (optional) */}
            <TextField
              label="Reference (optional)"
              value={editReference}
              onChange={(e) => setEditReference(e.target.value)}
              fullWidth
            />

            {/* Edit Reason (required) */}
            <TextField
              label="Edit Reason"
              value={editReason}
              onChange={(e) => setEditReason(e.target.value)}
              fullWidth
              required
              multiline
              rows={2}
              error={editReason.trim() === '' && saving}
              helperText="Required — explain why this receipt is being edited"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleEditSubmit}
            variant="contained"
            disabled={editSubmitDisabled}
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReceiptDetail;
